import { initializeApollo } from '@sortlist-frontend/data-fetching/public';
import { Log, Monitor } from '@sortlist-frontend/mlm';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import {
  assertNonEmptyString,
  assertNonEmptyStringOrNull,
  HttpBadRequest,
  HttpNotFound,
  isNonEmptyArray,
  isNonEmptyPlainObject,
} from '@sortlist-frontend/utils';
import { dehydrate, DehydratedState, QueryClient } from '@tanstack/react-query';
import { getCookie } from 'cookies-next';
/* eslint-disable sonarjs/cognitive-complexity */
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { DomainInfo } from '_backend/integration/domain-info';
import { PageHead } from '_components/base/head';
import { PublicGoogleTagManager } from '_components/common';
import { Public404Page } from '_components/error/public-404-page';
import { LayoutPagesRouterLight } from '_components/layout/LayoutPagesRouterLight';
import { LongtailJsonLd, PageSeoTag } from '_components/SEO';
import { buildLongtailJsonLd } from '_components/SEO/buildLongtailJsonLd';
import { PAGE_QUERY, type PageQueryResult } from '_components/SEO/hooks/useGetPage';
import { escapeJsonLd } from '_components/SEO/utils';
import { cacheConfig } from '_config/cache.config';
import { AppProviders } from '_core/app-providers';
import { PublicAppContextProvider } from '_core/context/public-app-context';
import { getUserConsentCookie } from '_core/cookies';
import { isSerializedError, SerializedError, serializeError } from '_core/error/error-serializer';
import { Expertise } from '_core/repos/expertises.repo';
import { getPublicApiRequestLink, publicApiRepo } from '_core/repos/public-api.repo';
import { Reviews } from '_core/repos/public-api.ssr.repo';
import { agenciesRepo } from '_core/repos/public-api/agencies.repo';
import { getCompanyLogos } from '_core/utils/company-logos';
import { baseSsrPrefetchQueries } from '_core/utils/public-api';
import { getServerSideTranslations } from '_core/utils/ssr';
import { toPublicQueryString } from '_core/utils/url/publicQuery';
import { isPathMatchingMiddleware } from '_core/utils/utils';
import { clientCompaniesData, CompanyType } from '_data/companies';
import { customConfig } from '_features/custom/custom.config';
import { getPageSeoMeta } from '_features/custom/getPageSeoMeta';
import { getCanonicalPageLink, getLongtailRedirect } from '_features/custom/longtailPageRelations';
import { buildPaginatedSeoMeta } from '_features/custom/paginated-seo';
import {
  AGENCY_CARDS_LAYOUT,
  AGENCY_CARDS_LAYOUT_NORMAL,
  LayoutType,
} from '_features/longtail/components/LayoutSwitch/constants';
import { getGraphqlSortBy, getJsonApiSortBy } from '_features/longtail/components/Sort/utils';
import { DirectoryPage, DirectoryPageData } from '_features/longtail/DirectoryPage';
import { getFiltersForGraphql } from '_features/longtail/sections/Filters/constants';
import { filtersToGraphqlFilters } from '_features/longtail/sections/Filters/utils';
import { getCurrentPage, getPageBriefingOptions, getServiceTopicId, LinkDetails } from '_features/longtail/utils';
import { findRankingPageKey, isRankingCandidateQuery } from '_features/RankingPage/data';
import { getRankingPageServerSideProps } from '_features/RankingPage/ssr/getRankingPageServerSideProps';
import type { RankingPageSsrData, RankingSeoProps } from '_features/RankingPage/ssr/types';
import type { RankingPageContent } from '_features/RankingPage/types';
import {
  Agencies,
  CardsSectionOptions,
  Domain,
  Links,
  ORGANIC,
  Page,
  PAID,
  Works,
  WorksSectionOptions,
} from '_types/public-api';

import { config } from '../middleware';

const RankingPage = dynamic(async () => (await import('_features/RankingPage')).RankingPage);
const RankingJsonLd = dynamic(async () => (await import('_components/SEO/RankingJsonLd')).RankingJsonLd);

type Prop = {
  data?: DirectoryPageData | null;
  rankingContent?: RankingPageContent | null;
  rankingData?: RankingPageSsrData | null;
  rankingSeo?: RankingSeoProps | null;
  locale?: string;
  canonical: string;
  query?: string;
  origin?: string;
  userConsent: string | null;
  error: SerializedError | null;
  page?: string;
  sort?: string;
  url?: string;
  publicApiNavBarUrl?: string;
  embed?: boolean;
  agencyCardsLayout?: LayoutType;
  abValue?: string;
  noFiltersResults?: boolean;
  dehydratedState?: DehydratedState;
  jsonLd?: string | null;
  alternateLinks?: LinkDetails[];
};

type PageOptions = {
  hidePaidSection: boolean;
  cardsOptions?: CardsSectionOptions;
  worksOptions?: WorksSectionOptions;
};

// When filtering let's keep only 20 limit
// Graphql query complexity increases with the number of agencies
// const { limit } = getOffsetAndLimitByPage(parseInt(page), cardsOptions?.limit);
const limit = 20;

const prefetchAllQuery = async (
  url: string,
  pageInfo: Page,
  queryParams: string,
  options: PageOptions,
  origin: string,
  locale: string,
  domain: Domain,
) => {
  const { hidePaidSection, cardsOptions, worksOptions } = options;
  const params = new URLSearchParams(queryParams);
  const after = params.get('after');
  const before = params.get('before');
  const page = params.get('page') ?? '1';
  const sort = params.get('sort') ?? 'position';

  const { host, path } = pageInfo.data.attributes;
  const agencyFilters = getFiltersForGraphql(locale, []); // Pass the t function directly from props/context
  let filters = {};

  try {
    filters = JSON.parse(params.get('filters') ?? '{}');
    if (isNonEmptyPlainObject(filters)) {
      const serviceTopicId = getServiceTopicId(pageInfo);
      filters = filtersToGraphqlFilters(
        agencyFilters,
        JSON.parse(params.get('filters') ?? '{}'),
        serviceTopicId as string,
        domain as Domain,
        locale,
      );
    }
  } catch (e) {
    Monitor.captureException(
      `Malformed filters ${JSON.stringify({
        url,
        filters,
      })}, ERROR: ${(e as Error).message}`,
    );
  }
  const agencyQueries = [];
  const paidProvidersLimit = cardsOptions?.paid_providers_limit ?? 6;

  if (Object.keys(filters).length > 0 || sort !== 'position') {
    agencyQueries.push(
      agenciesRepo.getAgencies(host, path, locale, origin, limit, after, before, getGraphqlSortBy(sort), {
        ...filters,
        sections: [ORGANIC],
      }),
    );
    agencyQueries.push(
      agenciesRepo.getAgencies(host, path, locale, origin, paidProvidersLimit, null, null, sort, {
        ...filters,
        sections: [PAID],
      }),
    );
  } else {
    // If someone ask for specific page we return the non filter endpoints until we fix this behaviour
    agencyQueries.push(
      publicApiRepo.getAgencies({
        page: url,
        section: 'organic',
        number: page,
        sort: getJsonApiSortBy(sort), // careful that sort is broken on json api
        cardsOptions,
        locale,
      }),
    );
    agencyQueries.push(
      publicApiRepo.getAgencies({
        page: url,
        section: 'paid',
        number: '1',
        sort: 'position',
        limit: paidProvidersLimit,
        locale,
      }),
    );
  }

  const queries = [
    agencyQueries[0],
    publicApiRepo.getWorks({ page: url, limit: Number(worksOptions?.limit ?? 3) }),
    publicApiRepo.getReviews(),
    getCompanyLogos(clientCompaniesData, 8),
  ];
  const paidAgenicesQuery = !hidePaidSection ? agencyQueries[1] : undefined;
  return await Promise.all([...queries, paidAgenicesQuery]);
};

const Route = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const {
    data,
    error,
    locale = 'en',
    canonical,
    query,
    origin = '',
    sort,
    agencyCardsLayout,
    noFiltersResults,
    jsonLd,
    alternateLinks = [],
    rankingContent,
    rankingData,
    rankingSeo,
  } = props;
  const { t } = useTranslation(customConfig.i18nNamespaces);
  const router = useRouter();
  const alternatesLinks = rankingData?.alternateLinks ?? alternateLinks;

  if (error != null && Boolean(isSerializedError(error))) {
    const { status } = error;
    if (status === 404) return <Public404Page message={t('common:pageErrors.occured')} locale={locale} />;
  }

  if (rankingContent != null && rankingData != null && rankingSeo != null) {
    const navigationData = {
      origin,
      locale,
      resolvedUrl: router.asPath,
      query,
      languageSwitcthRedirectHome: true,
      expertiseId: rankingData.page?.data.attributes.briefing_options?.expertise?.id,
      domain: rankingData.domain ?? undefined,
    };

    return (
      <PublicAppContextProvider
        canonical={canonical}
        origin={origin}
        locale={locale}
        briefingOptions={rankingData.page != null ? getPageBriefingOptions(rankingData.page) : undefined}>
        <PageHead
          showAlternates
          alternateLinks={alternatesLinks}
          canonical={canonical}
          query={query}
          origin={origin}
          locale={locale}
        />
        <PageSeoTag
          title={rankingSeo.title}
          description={rankingSeo.description}
          nextSeoProps={{
            openGraph: { type: 'article', title: rankingSeo.ogTitle, description: rankingSeo.ogDescription },
            nofollow: !rankingSeo.isIndexable,
            noindex: !rankingSeo.isIndexable,
          }}
          canonical={canonical}
        />
        <RankingJsonLd content={rankingContent} canonical={canonical} />
        <PublicGoogleTagManager />
        <LayoutPagesRouterLight navigationData={navigationData} data={rankingData}>
          <RankingPage content={rankingContent} navigationData={navigationData} page={rankingData.page} />
        </LayoutPagesRouterLight>
      </PublicAppContextProvider>
    );
  }

  if (data != null) {
    const { page: pageInfo } = data;
    const navigationData = {
      origin,
      locale,
      resolvedUrl: router.asPath,
      query,
      languageSwitcthRedirectHome: true,
      agencyCardsLayoutValue: agencyCardsLayout,
      sort,
      expertiseId: data?.page?.data?.attributes?.briefing_options?.expertise?.id,
      noFiltersResults,
      domain: data?.domain,
    };

    const currentPage = getCurrentPage(query);
    const pageSeo = getPageSeoMeta(pageInfo);
    const paginatedSeo = buildPaginatedSeoMeta(pageSeo.title, pageSeo.description, currentPage, t);

    // Paginated pages (page > 1) point their canonical at page 1's root URL so ranking signals
    // consolidate there instead of fragmenting across `?page=N` variants.
    return (
      <PublicAppContextProvider
        canonical={canonical}
        origin={origin ?? ''}
        locale={locale || 'en'}
        briefingOptions={getPageBriefingOptions(pageInfo)}>
        <PageHead
          showAlternates={currentPage <= 1}
          alternateLinks={alternatesLinks}
          canonical={canonical}
          query={query}
          origin={origin}
          locale={locale as string}
        />
        <PageSeoTag
          title={paginatedSeo.title}
          description={paginatedSeo.description}
          nextSeoProps={{
            nofollow: pageInfo.data.attributes.indexable !== true,
            noindex: pageInfo.data.attributes.indexable !== true,
          }}
          canonical={canonical}
        />
        {jsonLd != null ? <LongtailJsonLd jsonLd={jsonLd} /> : null}
        <PublicGoogleTagManager />
        <LayoutPagesRouterLight navigationData={navigationData} data={data}>
          <DirectoryPage data={data} navigationData={navigationData} t={t} />
        </LayoutPagesRouterLight>
      </PublicAppContextProvider>
    );
  }

  return <Public404Page message={t('common:pageErrors.occured')} locale={locale} />;
};

export default function CustomRoute(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <AppProviders dehydratedState={props.dehydratedState}>
      <Route {...props} />
    </AppProviders>
  );
}

const cacheOptions = cacheConfig.pages['longtails'] ?? { httpCacheControl: '' };

export const getServerSideProps: GetServerSideProps<Prop> = async (context) => {
  // Sometimes /next/chunks end here because of browser caching some chunks that are not aknowledged anymore by the server
  // We need to make them 404
  // See more: https://sortlist.sentry.io/issues/6125830352/?environment=production&project=5191789&query=&referrer=issue-stream&statsPeriod=30d&stream_index=0
  if (isPathMatchingMiddleware(context.resolvedUrl, config.matcher[0]) === false) {
    context.res.statusCode = 404;
    return {
      props: {
        error: serializeError(new HttpNotFound({ message: 'Page not found', url: context.resolvedUrl })),
      } as unknown as Prop,
    };
  }

  const { resolvedUrl, query, res, req } = context;

  // using resolvedUrl instead of context.query so that the pathname is not included
  // eg. for www.sortlist.fr/s/communication-de-crise/france-fr?page=2
  // query = { page: '2', custom: [ 's', 'communication-de-crise', 'france-fr' ] }

  const paramString = resolvedUrl.split('?')[1];
  const urlSearchParams = new URLSearchParams(paramString);
  const queryParams = toPublicQueryString(urlSearchParams);

  const { sort = 'position', page = '1', filters } = query;
  const origin = req.headers['sortlist-origin'];
  const locale = req.headers['sortlist-locale'];
  const canonical = req.headers['sortlist-canonical'];
  const hidePaidSection = req.headers['sortlist-hide-paid-section'] === 'true';

  const { i18nNamespaces } = customConfig;

  // example of retrieving the user consent that was set by the user in the front-end
  const userConsent = getUserConsentCookie(req);

  let error: SerializedError | null = null;

  assertNonEmptyString(origin, new HttpBadRequest({ message: 'Missing [origin] parameter' }));
  assertNonEmptyString(locale, new HttpBadRequest({ message: 'Missing [locale] parameter' }));
  assertNonEmptyString(canonical, new HttpBadRequest({ message: 'Missing [canonical] parameter' }));
  assertNonEmptyStringOrNull(sort, new HttpBadRequest({ message: '[sort] empty or multiple values found' }));
  assertNonEmptyStringOrNull(page, new HttpBadRequest({ message: '[page] empty or multiple values found' }));

  const embed = resolvedUrl.includes('/embed');
  const url = getPublicApiRequestLink(origin, locale, context.resolvedUrl.replace('/hidden/', '/'));

  const rankingPageKey =
    !embed && isRankingCandidateQuery(urlSearchParams)
      ? findRankingPageKey({ origin, locale, pathname: resolvedUrl.replace('/hidden/', '/') })
      : null;

  if (rankingPageKey != null) {
    return getRankingPageServerSideProps(context, { rankingPageKey, origin, locale, canonical, queryParams });
  }

  const queryClient = new QueryClient();

  let organicAgencies, works, reviews, companies, paidAgencies, links, homepageLinks, expertises, domain;
  let alternateLinks: LinkDetails[] = [];
  let canonicalLink: string | null = null;
  let publicApiNavBarUrl = '';
  let agencyCardsLayout = getCookie(AGENCY_CARDS_LAYOUT, { req, res });
  let pageInfo;
  let apolloClient;
  let pageQueryData: PageQueryResult;
  let noFiltersResults = false;
  try {
    pageInfo = await publicApiRepo.getPage({ page: `${url}?include=canonical_page,redirection_page,topics` });
    const { frontend_type: frontendType } = pageInfo.data.attributes;

    const redirect = getLongtailRedirect(pageInfo, url);
    if (redirect != null) return redirect;

    if (!getPageSeoMeta(pageInfo).hasOwnMetadata) {
      Monitor.captureMessage('[longtail] Page metadata has no title_tag or metadescription', {
        level: 'error',
        extra: { url, metadata: pageInfo.data.attributes.metadata },
        tags: { page: 'longtail', issue: 'missing_seo_metadata' },
      });
    }

    canonicalLink = getCanonicalPageLink(pageInfo);

    const { page_options } = pageInfo.data.attributes;
    const { cards: cardsOptions, works: worksOptions } = page_options ?? {};

    agencyCardsLayout = cardsOptions?.layout ?? agencyCardsLayout ?? AGENCY_CARDS_LAYOUT_NORMAL;

    const response = await baseSsrPrefetchQueries(origin, locale, context.resolvedUrl, queryClient, true);
    publicApiNavBarUrl = response.publicApiNavBarUrl;
    homepageLinks = response.homeLinks;
    links = response.links;
    alternateLinks = response.alternateLinks;
    expertises = response.expertises;
    domain = response.domain;

    [organicAgencies, works, reviews, companies, paidAgencies] = await prefetchAllQuery(
      url,
      pageInfo,
      queryParams,
      {
        hidePaidSection,
        cardsOptions,
        worksOptions,
      },
      origin,
      locale,
      domain as Domain,
    );

    let filtersObject = {};
    try {
      if (filters != null) {
        filtersObject = JSON.parse(filters as string);
      }
    } catch (e) {
      Log.error('Error parsing filters in longtails', { error: e });
    }

    apolloClient = initializeApollo({ schema: 'public' });

    const params = new URLSearchParams(queryParams);
    const after = params.get('after');
    const before = params.get('before');

    try {
      const limitOrganic = 20; // See apps/public/src/pages/[...custom].tsx L 97
      const limitPaid = cardsOptions?.paid_providers_limit ?? 6; // See apps/public/src/pages/[...custom].tsx L 140
      const pageQueryResult = await apolloClient.query({
        query: PAGE_QUERY,
        variables: {
          before,
          after,
          host: pageInfo?.data.attributes.host,
          locale: pageInfo?.data.attributes.locale,
          path: pageInfo?.data.attributes.path,
          lastOrganic: before != null ? limitOrganic : undefined,
          firstOrganic: before == null ? limitOrganic : undefined,
          lastPaid: before != null ? limitPaid : undefined,
          firstPaid: before == null ? limitPaid : undefined,
        },
      });
      pageQueryData = pageQueryResult.data;
    } catch (e) {
      // Ignore errors here as they are only used for JSONLD - we still want to render the page
      Log.error('Failed to fetch data for JSONLD (longtail):', { errorMessage: (e as Error).message });
    }

    const isSeaPage = frontendType === 'google_ads_visibility';
    const hasOrganicResults = isNonEmptyArray((organicAgencies as Agencies)?.data);
    const hasPaidResults = isNonEmptyArray((paidAgencies as Agencies)?.data);
    const hasFiltersApplied = Object.keys(filtersObject).length > 0;

    if (hasFiltersApplied && !hasOrganicResults && !hasPaidResults) {
      paidAgencies = await publicApiRepo.getAgencies({
        page: url,
        section: 'paid',
        number: '1',
        sort: 'position',
        limit: 6,
        locale,
      });
      noFiltersResults = true;
    }

    // return a 404 when there are no organic agencies
    // ie. for wrong page numbers
    if (!isSeaPage && !hasOrganicResults && page !== '1' && !noFiltersResults) {
      throw new HttpNotFound({ message: 'No agencies found', url });
    }
  } catch (e) {
    // Limitation: Typescript exceptions are not typed, so we rely on what is supposed to happen
    const status = (e as { status?: number }).status;
    // 404 and 410 must be served with their own status code: rethrowing them would make
    // Next.js respond with a 500.
    if (status !== 404 && status !== 410) {
      throw e;
    }

    // @TODO: use a more elegant way when upgrade to NextJS 10 https://nextjs.org/blog/next-10#redirect-and-notfound-support-for-getstaticprops--getserversideprops
    context.res.statusCode = status;
    error = serializeError(e as Error);
  }

  if (!context.res.headersSent) {
    context.res.setHeader('Cache-Control', cacheOptions.httpCacheControl);
  }

  const agencySlugsForPage = [
    ...((paidAgencies as Agencies)?.included ?? []),
    ...((organicAgencies as Agencies)?.included ?? []),
  ]
    .map((agency) => agency.attributes.slug)
    .filter((slug) => slug != null);
  const cacheTags = [...new Set(agencySlugsForPage.flat())];

  context.res.setHeader('X-Cache-Tag', cacheTags.join(','));

  const data =
    pageInfo != null && error == null
      ? {
          page: pageInfo as Page,
          organicAgencies: organicAgencies as Agencies,
          works: works as Works,
          reviews: reviews as Reviews,
          companies: companies as CompanyType[],
          paidAgencies: paidAgencies as Agencies,
          links: links as Links,
          homepageLinks: homepageLinks as Links,
          expertises: expertises as Expertise[],
          domain: domain as Domain,
        }
      : null;

  if (data == null && context.res.statusCode === 200) {
    context.res.statusCode = 404;
  }

  const resolvedCanonical = canonicalLink ?? canonical;

  // Build the JSON-LD on the server from the data already fetched, so the structured
  // data ships as a static <script> instead of forcing the whole Apollo cache to be
  // serialized into __NEXT_DATA__ and re-hydrated on the client.
  const domainInfo = DomainInfo.getFromOrigin(origin);
  const isFirstPage = getCurrentPage(queryParams) <= 1;
  const jsonLd =
    data != null
      ? escapeJsonLd(
          JSON.stringify(
            buildLongtailJsonLd({
              data,
              pageQueryData,
              canonical: resolvedCanonical,
              origin: domainInfo?.getOriginUrl() ?? '',
              locale,
              localeIso: domainInfo?.getIso31661() ?? null,
              pathname: resolvedUrl,
              // The visible FAQ is hidden on paginated pages, so omit the FAQPage node there too.
              includeFaq: isFirstPage,
            }),
          ),
        )
      : null;

  return {
    props: {
      data,
      locale,
      canonical: resolvedCanonical,
      query: queryParams,
      origin,
      dehydratedState: dehydrate(queryClient),
      userConsent,
      error,
      page,
      sort,
      url,
      publicApiNavBarUrl,
      embed,
      agencyCardsLayout: (agencyCardsLayout ?? '') as LayoutType,
      noFiltersResults,
      jsonLd,
      alternateLinks,
      ...(await getServerSideTranslations(locale, i18nNamespaces)),
    },
  };
};
